import is from '@sindresorhus/is';
import { useEffect } from 'react';

import { useTilingEngine, useTilingScroll } from '~/ui/domain/tiling-engine/TilingEngineContext';

import { useActiveThreadStore } from '../job-profile-section/comments/ActiveThreadStoreProvider';
import { COMMENTS_RAIL_WIDTH_PX } from '../job-profile-section/comments/constants';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';

const PROFILE_TILE_BASE_WIDTH = '50%';
const SCROLL_RAIL_INTO_VIEW_DELAY_MS = 250;
const RAIL_OPEN_SETTLE_FALLBACK_MS = 1000;

type ScrollToTile = ReturnType<typeof useTilingScroll>['scrollToTile'];
type SetRailOpening = (opening: boolean) => void;

const isTileSurfaceScrollEnd = (target: EventTarget | null): boolean => {
    const tile = document.querySelector(`[data-tile-id="${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}"]`);
    return target instanceof HTMLElement && !is.null(tile) && target !== tile && target.contains(tile);
};

const scrollRailIntoViewAndHoldComposer = (
    scrollToTile: ScrollToTile,
    setRailOpening: SetRailOpening,
): (() => void) => {
    setRailOpening(true);
    const abortController = new AbortController();
    let settled = false;
    let fallbackHandle: number | undefined;

    const finishOpening = (): void => {
        if (settled) return;
        settled = true;
        abortController.abort();
        if (!is.undefined(fallbackHandle)) window.clearTimeout(fallbackHandle);
        setRailOpening(false);
    };

    const scrollHandle = window.setTimeout(() => {
        document.addEventListener(
            'scrollend',
            (event) => {
                if (isTileSurfaceScrollEnd(event.target)) finishOpening();
            },
            { capture: true, signal: abortController.signal },
        );
        fallbackHandle = window.setTimeout(finishOpening, RAIL_OPEN_SETTLE_FALLBACK_MS);
        void scrollToTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY, { align: 'end' });
    }, SCROLL_RAIL_INTO_VIEW_DELAY_MS);

    return () => {
        window.clearTimeout(scrollHandle);
        finishOpening();
    };
};

export const useCommentsRailReveal = (isCommentsOpen: boolean): void => {
    const { updateTile } = useTilingEngine();
    const { scrollToTile } = useTilingScroll();
    const setRailOpening = useActiveThreadStore((state) => state.setRailOpening);

    useEffect(() => {
        const width = isCommentsOpen
            ? `calc(${PROFILE_TILE_BASE_WIDTH} + ${COMMENTS_RAIL_WIDTH_PX}px)`
            : PROFILE_TILE_BASE_WIDTH;
        updateTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY, { maxWidth: width, minWidth: width });
    }, [isCommentsOpen, updateTile]);

    useEffect(() => {
        if (!isCommentsOpen) return;
        return scrollRailIntoViewAndHoldComposer(scrollToTile, setRailOpening);
    }, [isCommentsOpen, scrollToTile, setRailOpening]);
};
